import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import React, { ComponentType, useEffect } from 'react';

import { routes } from 'src/constants/navigation';
import { useRedirectHome } from 'src/utils/hooks/use-redirect-home';

import PageLoading from '../../common/page-loading/page-loading';
import { useStores } from '../../common/root-store-provider/root-store-provider';
import page from '../page/page';

const nonMemberPage = <T extends object>(Component: ComponentType<T>) =>
  page(
    observer((props: T) => {
      const router = useRouter();
      const { pathname, query, isReady } = router;
      const { authStore, quizStore, paymentsStore, analyticsStore } =
        useStores();
      const { user, auth_token } = authStore;

      const resetQuizAndLogout =
        pathname === routes.quiz && isReady && !!query['reset'];
      const redirect =
        user != null &&
        // Pages from which payments are made have their own check for
        // an active subscription: they display a non-dismissible modal
        // if the subscription is active
        pathname !== routes.newPlanPaywall &&
        !!user.email &&
        // The rest of the non-member routes for users without
        // an active subscription must be listed here
        !resetQuizAndLogout;

      const isLoading = resetQuizAndLogout || redirect;

      useEffect(() => {
        if (resetQuizAndLogout) {
          localStorage.clear();
          sessionStorage.clear();
          analyticsStore.reset();
          quizStore.reset();
          authStore.logOut(auth_token!).then((r) => r);
          paymentsStore.resetPayment();
        }
      }, [
        analyticsStore,
        authStore,
        auth_token,
        paymentsStore,
        quizStore,
        resetQuizAndLogout,
      ]);

      useEffect(
        () => {
          if (resetQuizAndLogout && !user) {
            const { pathname, query } = router;
            const newQuery = { ...query };
            delete newQuery['reset'];
            // noinspection JSIgnoredPromiseFromCall
            router.replace({ pathname, query: newQuery });
          }
        },
        // https://github.com/vercel/next.js/issues/18127
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [resetQuizAndLogout, user],
      );

      useRedirectHome(redirect);

      return isLoading ? <PageLoading /> : <Component {...props} />;
    }),
  );

export default nonMemberPage;
